// Manager/Admin "Inspection Completion Report" — answers the contractual question:
// "Are we performing the 12 monthly irrigation inspections per property?" Aspire
// generates 12 monthly "Irrigation Check" work tickets per property (the obligation);
// each should have a completed inspection report in our app (the completion). This
// report joins the two and surfaces the gaps. Pure read surface over
// GET /api/dashboard/inspection-completion (queries Aspire — can be slow / 503).
// Print- and CSV-export friendly. Style mirrors ComplianceReport.tsx.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  AlertTriangle,
  CalendarCheck,
  CalendarClock,
  CheckCircle2,
  ClipboardCheck,
  ClipboardX,
  Clock,
  Download,
  FileWarning,
  Lightbulb,
  Link2Off,
  Loader2,
  MapPin,
  Printer,
  RefreshCw,
  Search,
  X,
} from 'lucide-react';
import { apiClient, ApiError } from '../lib/apiClient';
import {
  Card,
  Donut,
  Legend,
  TrendChart,
  StatTile,
  Pill,
  type DonutSegment,
  type TrendSeries,
  type Tone,
} from './dashboard/charts';

// ---------------------------------------------------------------------------
// Endpoint shapes (exact response contract)
// ---------------------------------------------------------------------------
type ReportCompletionStatus = 'approved' | 'submitted' | 'draft' | 'none';

interface CompletionMonthCell {
  month: string;
  ticketId: number;
  ticketNumber: number | null;
  scheduledDate: string;
  due: boolean;
  canceled: boolean;
  aspireStatus: string | null;
  reportStatus: ReportCompletionStatus;
  reportId: string | null;
}

interface CompletionProperty {
  propertyId: number;
  jobSiteId: string | null;
  name: string;
  branch: string | null;
  linked: boolean;
  expected: number;
  submitted: number;
  approved: number;
  missing: number;
  upcoming: number;
  canceled: number;
  completionRate: number;
  months: CompletionMonthCell[];
}

interface InspectionCompletionData {
  generated_at: string;
  from: string;
  to: string;
  months: string[]; // 'YYYY-MM' buckets ascending
  totals: {
    properties: number;
    expected: number;
    submitted: number;
    approved: number;
    missing: number;
    upcoming: number;
    canceled: number;
    aspireCompleteNoReport: number;
    completionRate: number;
    approvalRate: number;
    unlinkedProperties: number;
    unlinkedTickets: number;
  };
  byMonth: Array<{ month: string; expected: number; submitted: number; approved: number; missing: number }>;
  properties: CompletionProperty[];
}

// ---------------------------------------------------------------------------
// The visual status of a single heatmap cell, derived from a CompletionMonthCell
// (or its absence). Drives both colour and tooltip/legend.
// ---------------------------------------------------------------------------
type CellKind = 'approved' | 'submitted' | 'missing' | 'upcoming' | 'canceled' | 'none';

const CELL_COLOR: Record<CellKind, string> = {
  approved: '#059669', // emerald-600
  submitted: '#3b82f6', // blue-500
  missing: '#dc2626', // red-600
  upcoming: '#e5e7eb', // gray-200
  canceled: '#f3f4f6', // gray-100 (neutral light — canceled is not an obligation)
  none: '#fafafa', // very light — no ticket that month
};

// Tailwind background classes mirroring CELL_COLOR (used for crisp small squares).
const CELL_BG: Record<CellKind, string> = {
  approved: 'bg-emerald-600',
  submitted: 'bg-blue-500',
  missing: 'bg-red-600',
  upcoming: 'bg-gray-200', // theme-allow: heatmap data-viz status color
  canceled: 'bg-gray-100 [background-image:repeating-linear-gradient(45deg,transparent,transparent_2px,rgba(148,163,184,0.4)_2px,rgba(148,163,184,0.4)_3px)]', // theme-allow: heatmap data-viz status color
  none: 'bg-gray-50 border border-dashed border-gray-200', // theme-allow: heatmap data-viz status color
};

function cellKind(cell: CompletionMonthCell | undefined): CellKind {
  if (!cell) return 'none';
  if (cell.canceled) return 'canceled';
  if (cell.reportStatus === 'approved') return 'approved';
  if (cell.reportStatus === 'submitted') return 'submitted';
  // Not yet submitted: due → missing; future → upcoming.
  if (!cell.due) return 'upcoming';
  return 'missing'; // due & (none | draft)
}

// Report status → pill tone.
const REPORT_TONE: Record<ReportCompletionStatus, Tone> = {
  approved: 'emerald',
  submitted: 'blue',
  draft: 'amber',
  none: 'red',
};

// ---------------------------------------------------------------------------
// Date helpers — parse 'YYYY-MM' manually (NEVER pass to Date directly).
// ---------------------------------------------------------------------------
function parseMonthBucket(bucket: string): { year: number; monthIdx: number } | null {
  const m = /^(\d{4})-(\d{2})$/.exec(bucket);
  if (!m) return null;
  return { year: Number(m[1]), monthIdx: Number(m[2]) - 1 };
}

function formatMonthShort(bucket: string, locale: string): string {
  const p = parseMonthBucket(bucket);
  if (!p) return bucket;
  return new Date(p.year, p.monthIdx).toLocaleString(locale, { month: 'short' });
}

function formatMonthLong(bucket: string, locale: string): string {
  const p = parseMonthBucket(bucket);
  if (!p) return bucket;
  return new Date(p.year, p.monthIdx).toLocaleString(locale, { month: 'long', year: 'numeric' });
}

function pct(rate: number): number {
  return Math.round((rate || 0) * 100);
}

function completionTone(rate: number): Tone {
  const p = pct(rate);
  if (p >= 90) return 'emerald';
  if (p >= 70) return 'amber';
  return 'red';
}

type SortKey = 'missing' | 'completion' | 'name';
type RowFilter = 'all' | 'missing' | 'unlinked' | 'aspireCompleteNoReport';

// Find the cell for a given month bucket on a property (may be absent).
function cellForMonth(property: CompletionProperty, monthBucket: string): CompletionMonthCell | undefined {
  return property.months.find((c) => c.month === monthBucket);
}

// Does this property have at least one month where Aspire is Complete but no report?
function propertyHasAspireCompleteNoReport(p: CompletionProperty): boolean {
  return p.months.some(
    (c) =>
      !c.canceled &&
      (c.aspireStatus ?? '').toLowerCase() === 'complete' &&
      (c.reportStatus === 'none' || c.reportStatus === 'draft'),
  );
}

export function InspectionCompletionReport() {
  const { t, i18n } = useTranslation('common');
  const locale = i18n.language;

  const [data, setData] = useState<InspectionCompletionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notConfigured, setNotConfigured] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // table controls
  const [sortKey, setSortKey] = useState<SortKey>('missing');
  const [rowFilter, setRowFilter] = useState<RowFilter>('all');
  const [search, setSearch] = useState('');
  const [detail, setDetail] = useState<CompletionProperty | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setNotConfigured(false);
    apiClient
      .get<InspectionCompletionData>('/api/dashboard/inspection-completion')
      .then((res) => {
        if (cancelled) return;
        setData(res);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 503 && err.code === 'aspire_not_configured') {
          setNotConfigured(true);
          return;
        }
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const months = data?.months ?? [];

  // Filtered + sorted property rows for the grid.
  const rows = useMemo<CompletionProperty[]>(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    let out = data.properties.filter((p) => {
      if (q && !p.name.toLowerCase().includes(q)) return false;
      switch (rowFilter) {
        case 'missing':
          return p.missing > 0;
        case 'unlinked':
          return !p.linked;
        case 'aspireCompleteNoReport':
          return propertyHasAspireCompleteNoReport(p);
        default:
          return true;
      }
    });
    out = [...out].sort((a, b) => {
      switch (sortKey) {
        case 'completion':
          return a.completionRate - b.completionRate || b.missing - a.missing;
        case 'name':
          return a.name.localeCompare(b.name);
        case 'missing':
        default:
          return b.missing - a.missing || a.completionRate - b.completionRate || a.name.localeCompare(b.name);
      }
    });
    return out;
  }, [data, search, rowFilter, sortKey]);

  // (a) Missing inspections — worst first.
  const missingProps = useMemo(
    () => (data ? data.properties.filter((p) => p.missing > 0).sort((a, b) => b.missing - a.missing) : []),
    [data],
  );

  // (b) Aspire-complete-no-report — flatten to the specific (property × month) cells.
  const aspireGapCells = useMemo(() => {
    if (!data) return [];
    const out: Array<{ property: CompletionProperty; cell: CompletionMonthCell }> = [];
    for (const p of data.properties) {
      for (const c of p.months) {
        if (
          !c.canceled &&
          (c.aspireStatus ?? '').toLowerCase() === 'complete' &&
          (c.reportStatus === 'none' || c.reportStatus === 'draft')
        ) {
          out.push({ property: p, cell: c });
        }
      }
    }
    return out.sort((a, b) => a.cell.month.localeCompare(b.cell.month) || a.property.name.localeCompare(b.property.name));
  }, [data]);

  // (c) Unlinked Aspire properties.
  const unlinkedProps = useMemo(
    () => (data ? data.properties.filter((p) => !p.linked).sort((a, b) => a.name.localeCompare(b.name)) : []),
    [data],
  );

  // The oldest month bucket that still has any missing inspection (for the rec text).
  const oldestMissingMonth = useMemo(() => {
    for (const m of months) {
      const any = data?.properties.some((p) => {
        const c = cellForMonth(p, m);
        return c && cellKind(c) === 'missing';
      });
      if (any) return m;
    }
    return null;
  }, [data, months]);

  // -------------------------------------------------------------------------
  // Recommendations — 3..5 auto-generated bullets.
  // -------------------------------------------------------------------------
  const recommendations = useMemo<string[]>(() => {
    if (!data) return [];
    const recs: string[] = [];
    const tot = data.totals;

    if (tot.missing > 0) {
      recs.push(
        t('inspectionCompletion.recMissing', {
          count: tot.missing,
          properties: missingProps.length,
          oldest: oldestMissingMonth ? formatMonthLong(oldestMissingMonth, locale) : '',
          defaultValue: oldestMissingMonth
            ? '{{count}} inspections are missing across {{properties}} properties — oldest is {{oldest}}.'
            : '{{count}} inspections are missing across {{properties}} properties.',
        }),
      );
    }

    if (tot.aspireCompleteNoReport > 0) {
      recs.push(
        t('inspectionCompletion.recAspireGap', {
          count: tot.aspireCompleteNoReport,
          defaultValue:
            '{{count}} tickets are marked Complete in Aspire but have no submitted report — create or submit those reports.',
        }),
      );
    }

    if (tot.unlinkedProperties > 0) {
      recs.push(
        t('inspectionCompletion.recUnlinked', {
          count: tot.unlinkedProperties,
          tickets: tot.unlinkedTickets,
          defaultValue:
            'Link {{count}} Aspire properties to the app ({{tickets}} tickets) so their inspections can be tracked.',
        }),
      );
    }

    if (pct(tot.completionRate) < 90 && tot.expected > 0) {
      recs.push(
        t('inspectionCompletion.recRate', {
          pct: pct(tot.completionRate),
          defaultValue:
            'Overall completion is {{pct}}% — below the 12-of-12 obligation. Prioritise the missing list above.',
        }),
      );
    }

    if (tot.upcoming > 0) {
      recs.push(
        t('inspectionCompletion.recUpcoming', {
          count: tot.upcoming,
          defaultValue: '{{count}} inspections are upcoming (not yet due) — schedule crews ahead of their due month.',
        }),
      );
    }

    return recs.slice(0, 5);
  }, [data, missingProps.length, oldestMissingMonth, locale, t]);

  // -------------------------------------------------------------------------
  // CSV export — per-property rollup + one column per month bucket (cell status).
  // -------------------------------------------------------------------------
  function exportCsv() {
    if (!data) return;
    const headers = [
      t('inspectionCompletion.csvProperty', 'Property'),
      t('inspectionCompletion.csvBranch', 'Branch'),
      t('inspectionCompletion.csvLinked', 'Linked'),
      t('inspectionCompletion.csvExpected', 'Expected'),
      t('inspectionCompletion.csvSubmitted', 'Submitted'),
      t('inspectionCompletion.csvApproved', 'Approved'),
      t('inspectionCompletion.csvMissing', 'Missing'),
      t('inspectionCompletion.csvCompletionPct', 'Completion %'),
      ...months.map((m) => formatMonthShort(m, locale)),
    ];
    const esc = (val: string) => `"${(val ?? '').replace(/"/g, '""')}"`;
    const lines = [headers.map(esc).join(',')];
    for (const p of rows) {
      const monthCols = months.map((m) => kindLabel(cellKind(cellForMonth(p, m))));
      lines.push(
        [
          p.name,
          p.branch ?? '',
          p.linked ? t('inspectionCompletion.yes', 'Yes') : t('inspectionCompletion.no', 'No'),
          String(p.expected),
          String(p.submitted),
          String(p.approved),
          String(p.missing),
          `${pct(p.completionRate)}%`,
          ...monthCols,
        ]
          .map(esc)
          .join(','),
      );
    }
    const blob = new Blob([`﻿${lines.join('\r\n')}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `inspection-completion-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  function kindLabel(k: CellKind): string {
    switch (k) {
      case 'approved':
        return t('inspectionCompletion.kindApproved', 'Approved');
      case 'submitted':
        return t('inspectionCompletion.kindSubmitted', 'Submitted');
      case 'missing':
        return t('inspectionCompletion.kindMissing', 'Missing');
      case 'upcoming':
        return t('inspectionCompletion.kindUpcoming', 'Upcoming');
      case 'canceled':
        return t('inspectionCompletion.kindCanceled', 'Canceled');
      case 'none':
        return t('inspectionCompletion.kindNone', 'No ticket');
    }
  }

  function reportStatusLabel(s: ReportCompletionStatus): string {
    switch (s) {
      case 'approved':
        return t('inspectionCompletion.reportApproved', 'Approved');
      case 'submitted':
        return t('inspectionCompletion.reportSubmitted', 'Submitted');
      case 'draft':
        return t('inspectionCompletion.reportDraft', 'Draft');
      case 'none':
        return t('inspectionCompletion.reportNone', 'No report');
    }
  }

  // -------------------------------------------------------------------------
  // Loading / error / not-configured / empty states.
  // -------------------------------------------------------------------------
  if (loading) {
    return (
      <div className="m-6 flex flex-col items-center justify-center rounded-xl border border-line bg-surface p-10 text-center shadow-sm">
        <Loader2 size={26} className="animate-spin text-sky-500" />
        <p className="mt-3 text-sm font-medium text-theme-primary">
          {t('inspectionCompletion.loadingTitle', 'Querying Aspire — this can take up to a minute…')}
        </p>
        <p className="mt-0.5 text-sm text-theme-secondary">
          {t('inspectionCompletion.loadingHint', 'We are joining 12 months of Irrigation Check tickets to your inspection reports.')}
        </p>
      </div>
    );
  }

  if (notConfigured) {
    return (
      <div className="m-6 flex flex-col items-center justify-center rounded-xl border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 p-10 text-center shadow-sm">
        <FileWarning size={28} className="text-amber-500 dark:text-amber-400" />
        <p className="mt-2 text-sm font-semibold text-amber-800 dark:text-amber-300">
          {t('inspectionCompletion.notConfiguredTitle', "Aspire isn't configured")}
        </p>
        <p className="mt-0.5 max-w-md text-sm text-amber-700 dark:text-amber-300">
          {t(
            'inspectionCompletion.notConfiguredHint',
            'This report reads Irrigation Check work tickets from Aspire. Add Aspire API credentials in Settings to enable it.',
          )}
        </p>
        <button
          type="button"
          onClick={() => setReloadKey((k) => k + 1)}
          className="mt-4 inline-flex items-center gap-1.5 rounded-lg border border-amber-300 dark:border-amber-500/30 bg-surface px-3 py-1.5 text-sm font-medium text-amber-800 dark:text-amber-300 shadow-sm hover:bg-amber-50 dark:hover:bg-amber-500/10"
        >
          <RefreshCw size={14} />
          {t('inspectionCompletion.retry', 'Retry')}
        </button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="m-6 flex items-start gap-2 rounded-xl border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 p-4 text-sm text-red-700 dark:text-red-300">
        <AlertTriangle size={16} className="mt-0.5 shrink-0" />
        <div className="flex-1">
          <p className="font-semibold">{t('inspectionCompletion.errorTitle', 'Could not load the inspection completion report')}</p>
          <p className="mt-0.5 text-red-600 dark:text-red-400">{error}</p>
          <button
            type="button"
            onClick={() => setReloadKey((k) => k + 1)}
            className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-red-300 dark:border-red-500/30 bg-surface px-3 py-1.5 text-sm font-medium text-red-700 dark:text-red-300 shadow-sm hover:bg-red-50 dark:hover:bg-red-500/10"
          >
            <RefreshCw size={14} />
            {t('inspectionCompletion.retry', 'Retry')}
          </button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const totals = data.totals;

  if (totals.expected === 0) {
    return (
      <div className="m-6 flex flex-col items-center justify-center rounded-xl border border-line bg-surface p-10 text-center shadow-sm">
        <CalendarCheck size={28} className="text-theme-secondary" />
        <p className="mt-2 text-sm font-medium text-theme-primary">
          {t('inspectionCompletion.emptyTitle', 'No obligated inspections in this window')}
        </p>
        <p className="mt-0.5 max-w-md text-sm text-theme-secondary">
          {t(
            'inspectionCompletion.emptyHint',
            'No due (non-canceled) Irrigation Check tickets were found for the last 12 months. Once Aspire schedules them, completion will appear here.',
          )}
        </p>
        {totals.unlinkedProperties > 0 && (
          <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
            {t('inspectionCompletion.emptyUnlinked', {
              count: totals.unlinkedProperties,
              defaultValue: '{{count}} Aspire properties are not linked in the app.',
            })}
          </p>
        )}
      </div>
    );
  }

  // Trend series across byMonth.
  const trendCategories = data.byMonth.map((b) => formatMonthShort(b.month, locale));
  const trendSeries: TrendSeries[] = [
    {
      key: 'expected',
      label: t('inspectionCompletion.seriesExpected', 'Expected'),
      color: '#9ca3af',
      points: data.byMonth.map((b) => b.expected),
    },
    {
      key: 'submitted',
      label: t('inspectionCompletion.seriesSubmitted', 'Submitted'),
      color: '#3b82f6',
      points: data.byMonth.map((b) => b.submitted),
    },
    {
      key: 'approved',
      label: t('inspectionCompletion.seriesApproved', 'Approved'),
      color: '#059669',
      points: data.byMonth.map((b) => b.approved),
    },
  ];

  const donutSegments: DonutSegment[] = [
    {
      key: 'approved',
      label: t('inspectionCompletion.kindApproved', 'Approved'),
      value: totals.approved,
      color: CELL_COLOR.approved,
    },
    {
      key: 'submitted',
      label: t('inspectionCompletion.donutSubmittedOnly', 'Submitted (not approved)'),
      value: Math.max(0, totals.submitted - totals.approved),
      color: CELL_COLOR.submitted,
    },
    {
      key: 'missing',
      label: t('inspectionCompletion.kindMissing', 'Missing'),
      value: totals.missing,
      color: CELL_COLOR.missing,
    },
  ];

  const generatedDate = new Date(data.generated_at).toLocaleString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 sm:p-6 print:p-0">
      {/* 1. Header --------------------------------------------------------- */}
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-bold text-theme-primary">
            <ClipboardCheck size={22} className="text-sky-600" />
            {t('inspectionCompletion.title', 'Inspection Completion Report')}
          </h1>
          <p className="mt-0.5 text-sm text-theme-secondary">
            {t('inspectionCompletion.generated', {
              date: generatedDate,
              from: formatMonthShort(data.from.slice(0, 7), locale),
              to: formatMonthShort(data.to.slice(0, 7), locale),
              defaultValue: 'Generated {{date}} · {{from}}–{{to}}',
            })}
          </p>
        </div>
        <div className="flex items-center gap-2 print:hidden">
          <button
            type="button"
            onClick={() => setReloadKey((k) => k + 1)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-1.5 text-sm font-medium text-theme-primary shadow-sm hover:bg-subtle"
          >
            <RefreshCw size={15} />
            {t('inspectionCompletion.refresh', 'Refresh')}
          </button>
          <button
            type="button"
            onClick={exportCsv}
            className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-1.5 text-sm font-medium text-theme-primary shadow-sm hover:bg-subtle"
          >
            <Download size={15} />
            {t('inspectionCompletion.exportCsv', 'Export CSV')}
          </button>
          <button
            type="button"
            onClick={() => window.print()}
            className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-1.5 text-sm font-medium text-theme-primary shadow-sm hover:bg-subtle"
          >
            <Printer size={15} />
            {t('inspectionCompletion.print', 'Print')}
          </button>
        </div>
      </header>

      {/* 2. Score strip --------------------------------------------------- */}
      <section className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <StatTile
          icon={<CheckCircle2 size={15} />}
          tone={completionTone(totals.completionRate)}
          label={t('inspectionCompletion.tileCompletion', 'Completion')}
          value={`${pct(totals.completionRate)}%`}
          sub={t('inspectionCompletion.tileCompletionSub', {
            submitted: totals.submitted,
            expected: totals.expected,
            defaultValue: '{{submitted}} of {{expected}} obligated',
          })}
        />
        <StatTile
          icon={<ClipboardCheck size={15} />}
          tone="emerald"
          label={t('inspectionCompletion.tileApproved', 'Approved rate')}
          value={`${pct(totals.approvalRate)}%`}
          sub={t('inspectionCompletion.tileApprovedSub', {
            count: totals.approved,
            defaultValue: '{{count}} approved',
          })}
        />
        <StatTile
          icon={<ClipboardX size={15} />}
          tone="red"
          label={t('inspectionCompletion.tileMissing', 'Missing')}
          value={totals.missing}
          sub={t('inspectionCompletion.tileMissingSub', 'due, no report')}
        />
        <StatTile
          icon={<CalendarClock size={15} />}
          tone={totals.upcoming > 0 ? 'sky' : 'gray'}
          label={t('inspectionCompletion.tileUpcoming', 'Upcoming')}
          value={totals.upcoming}
          sub={t('inspectionCompletion.tileUpcomingSub', 'not yet due')}
        />
        <StatTile
          icon={<AlertTriangle size={15} />}
          tone="amber"
          label={t('inspectionCompletion.tileAspireGap', 'Aspire done · no report')}
          value={totals.aspireCompleteNoReport}
          sub={t('inspectionCompletion.tileAspireGapSub', 'action needed')}
        />
        <StatTile
          icon={<MapPin size={15} />}
          tone="blue"
          label={t('inspectionCompletion.tileProperties', 'Properties')}
          value={totals.properties}
          sub={t('inspectionCompletion.tilePropertiesSub', 'tracked')}
        />
      </section>

      {/* Coverage note (unlinked) */}
      {totals.unlinkedProperties > 0 && (
        <p className="-mt-2 flex items-center gap-1.5 text-xs text-theme-secondary">
          <Link2Off size={13} className="text-amber-500" />
          {t('inspectionCompletion.coverageNote', {
            properties: totals.unlinkedProperties,
            tickets: totals.unlinkedTickets,
            defaultValue:
              '{{properties}} Aspire properties not linked in the app ({{tickets}} tickets) — excluded from the rate.',
          })}
        </p>
      )}

      {/* 3. Monthly trend + overall donut --------------------------------- */}
      <section className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card
          title={t('inspectionCompletion.trendTitle', 'Monthly trend')}
          icon={<CalendarCheck size={15} />}
          className="lg:col-span-2"
        >
          <TrendChart series={trendSeries} categories={trendCategories} />
          <div className="mt-2 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-theme-secondary">
            {trendSeries.map((s) => (
              <span key={s.key} className="inline-flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-sm" style={{ background: s.color }} />
                {s.label}
              </span>
            ))}
          </div>
        </Card>
        <Card title={t('inspectionCompletion.overallTitle', 'Overall')} icon={<ClipboardCheck size={15} />}>
          <div className="flex flex-wrap items-center gap-4">
            <Donut
              segments={donutSegments}
              centerValue={`${pct(totals.completionRate)}%`}
              centerLabel={t('inspectionCompletion.donutCenter', 'complete')}
            />
            <div className="min-w-[150px] flex-1">
              <Legend segments={donutSegments} total={totals.expected} />
            </div>
          </div>
        </Card>
      </section>

      {/* 4. Per-property completion grid (the core) ----------------------- */}
      <Card
        title={t('inspectionCompletion.gridTitle', 'Per-property completion')}
        icon={<MapPin size={15} />}
        action={
          <span className="text-xs font-normal text-theme-secondary">
            {t('inspectionCompletion.gridCount', {
              shown: rows.length,
              total: data.properties.length,
              defaultValue: '{{shown}} of {{total}}',
            })}
          </span>
        }
        bodyClassName="p-0"
      >
        {/* Controls */}
        <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-3 print:hidden">
          <div className="relative">
            <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-theme-secondary" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('inspectionCompletion.searchPlaceholder', 'Search property…')}
              className="w-48 rounded-lg border border-line bg-surface py-1.5 pl-8 pr-2 text-sm text-theme-primary focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
          </div>

          {/* Row filters */}
          {(
            [
              ['all', t('inspectionCompletion.filterAll', 'All')],
              ['missing', t('inspectionCompletion.filterMissing', 'Has missing')],
              ['unlinked', t('inspectionCompletion.filterUnlinked', 'Unlinked')],
              ['aspireCompleteNoReport', t('inspectionCompletion.filterAspireGap', 'Aspire done · no report')],
            ] as Array<[RowFilter, string]>
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setRowFilter(key)}
              className={`rounded-full px-2.5 py-1 text-xs font-semibold transition-colors ${
                rowFilter === key ? 'bg-blue-600 text-white' : 'bg-subtle text-theme-secondary hover:bg-subtle'
              }`}
            >
              {label}
            </button>
          ))}

          {/* Sort */}
          <div className="ml-auto flex items-center gap-1.5 text-xs text-theme-secondary">
            <span>{t('inspectionCompletion.sortBy', 'Sort')}</span>
            <select
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value as SortKey)}
              className="rounded-lg border border-line bg-surface px-2 py-1 text-xs text-theme-primary focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
            >
              <option value="missing">{t('inspectionCompletion.sortMissing', 'Missing (most)')}</option>
              <option value="completion">{t('inspectionCompletion.sortCompletion', 'Completion (lowest)')}</option>
              <option value="name">{t('inspectionCompletion.sortName', 'Name (A–Z)')}</option>
            </select>
          </div>
        </div>

        {/* Heatmap legend */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 border-b border-line px-4 py-2 text-xs text-theme-secondary">
          {(['approved', 'submitted', 'missing', 'upcoming', 'canceled', 'none'] as CellKind[]).map((k) => (
            <span key={k} className="inline-flex items-center gap-1.5">
              <span className={`inline-block h-3 w-3 rounded-sm ${CELL_BG[k]}`} />
              {kindLabel(k)}
            </span>
          ))}
        </div>

        {rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-4 py-10 text-center">
            <CheckCircle2 size={26} className="text-emerald-500" />
            <p className="mt-2 text-sm font-medium text-theme-primary">
              {t('inspectionCompletion.gridNoMatch', 'No properties match the current filters.')}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-theme-secondary">
                  <th className="px-4 py-2 font-medium">{t('inspectionCompletion.colProperty', 'Property')}</th>
                  <th className="px-3 py-2 text-right font-medium">{t('inspectionCompletion.colCompletion', 'Completion')}</th>
                  <th className="px-3 py-2 text-right font-medium">{t('inspectionCompletion.colMissing', 'Missing')}</th>
                  <th className="px-4 py-2 font-medium">
                    {t('inspectionCompletion.colMonths', 'Months')}
                    <span className="ml-1 hidden font-normal normal-case text-theme-secondary sm:inline">
                      {trendCategories.length > 0 ? `${trendCategories[0]}–${trendCategories[trendCategories.length - 1]}` : ''}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr
                    key={p.propertyId}
                    onClick={() => setDetail(p)}
                    className="cursor-pointer border-b border-line last:border-0 hover:bg-subtle"
                  >
                    <td className="px-4 py-2.5 align-top">
                      <div className="font-medium text-theme-primary">{p.name}</div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-1">
                        {p.branch && <Pill tone="gray">{p.branch}</Pill>}
                        {!p.linked && (
                          <Pill tone="amber" icon={<Link2Off size={11} />}>
                            {t('inspectionCompletion.unlinked', 'Unlinked')}
                          </Pill>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right align-top">
                      <span className={`font-semibold tabular-nums ${toneText(completionTone(p.completionRate))}`}>
                        {pct(p.completionRate)}%
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-right align-top tabular-nums">
                      {p.missing > 0 ? (
                        <span className="font-semibold text-red-600 dark:text-red-400">{p.missing}</span>
                      ) : (
                        <span className="text-theme-secondary">0</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 align-top">
                      <div className="flex flex-wrap gap-1">
                        {months.map((m) => {
                          const cell = cellForMonth(p, m);
                          const k = cellKind(cell);
                          const title = [
                            formatMonthLong(m, locale),
                            cell?.ticketNumber != null
                              ? t('inspectionCompletion.ttTicket', { num: cell.ticketNumber, defaultValue: 'Ticket #{{num}}' })
                              : t('inspectionCompletion.ttNoTicket', 'No ticket'),
                            cell?.aspireStatus
                              ? t('inspectionCompletion.ttAspire', { status: cell.aspireStatus, defaultValue: 'Aspire: {{status}}' })
                              : null,
                            cell
                              ? t('inspectionCompletion.ttReport', {
                                  status: reportStatusLabel(cell.reportStatus),
                                  defaultValue: 'Report: {{status}}',
                                })
                              : null,
                            t('inspectionCompletion.ttState', { state: kindLabel(k), defaultValue: 'State: {{state}}' }),
                          ]
                            .filter(Boolean)
                            .join(' · ');
                          return (
                            <span
                              key={m}
                              title={title}
                              className={`inline-block h-4 w-4 rounded-sm ${CELL_BG[k]}`}
                              aria-label={title}
                            />
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* 5. Action lists -------------------------------------------------- */}
      <section className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* (a) Missing inspections */}
        <Card title={t('inspectionCompletion.missingTitle', 'Missing inspections')} icon={<ClipboardX size={15} />}>
          {missingProps.length === 0 ? (
            <p className="py-6 text-center text-sm text-theme-secondary">
              {t('inspectionCompletion.missingNone', 'No missing inspections — every obligation is met.')}
            </p>
          ) : (
            <ul className="divide-y divide-line">
              {missingProps.slice(0, 12).map((p) => (
                <li key={p.propertyId} className="flex items-center justify-between gap-2 py-2">
                  <button
                    type="button"
                    onClick={() => setDetail(p)}
                    className="truncate text-left text-sm font-medium text-theme-primary hover:text-blue-700 print:hover:text-theme-primary"
                  >
                    {p.name}
                    {p.branch && <span className="ml-1 text-xs font-normal text-theme-secondary">{p.branch}</span>}
                  </button>
                  <Pill tone="red">
                    {t('inspectionCompletion.missingCount', { count: p.missing, defaultValue: '{{count}} missing' })}
                  </Pill>
                </li>
              ))}
              {missingProps.length > 12 && (
                <li className="pt-2 text-xs text-theme-secondary">
                  {t('inspectionCompletion.andMore', { count: missingProps.length - 12, defaultValue: '+{{count}} more' })}
                </li>
              )}
            </ul>
          )}
        </Card>

        {/* (b) Aspire marked Complete, no report */}
        <Card title={t('inspectionCompletion.aspireGapTitle', 'Aspire complete · no report')} icon={<AlertTriangle size={15} />}>
          {aspireGapCells.length === 0 ? (
            <p className="py-6 text-center text-sm text-theme-secondary">
              {t('inspectionCompletion.aspireGapNone', 'No mismatches — every completed ticket has a report.')}
            </p>
          ) : (
            <ul className="divide-y divide-line">
              {aspireGapCells.slice(0, 12).map(({ property, cell }) => (
                <li key={`${property.propertyId}-${cell.ticketId}`} className="flex items-center justify-between gap-2 py-2">
                  <button
                    type="button"
                    onClick={() => setDetail(property)}
                    className="min-w-0 text-left hover:text-blue-700 print:hover:text-inherit"
                  >
                    <div className="truncate text-sm font-medium text-theme-primary">{property.name}</div>
                    <div className="text-xs text-theme-secondary">
                      {formatMonthLong(cell.month, locale)}
                      {cell.ticketNumber != null
                        ? ` · ${t('inspectionCompletion.ticketShort', { num: cell.ticketNumber, defaultValue: '#{{num}}' })}`
                        : ''}
                    </div>
                  </button>
                  <Pill tone={REPORT_TONE[cell.reportStatus]}>{reportStatusLabel(cell.reportStatus)}</Pill>
                </li>
              ))}
              {aspireGapCells.length > 12 && (
                <li className="pt-2 text-xs text-theme-secondary">
                  {t('inspectionCompletion.andMore', { count: aspireGapCells.length - 12, defaultValue: '+{{count}} more' })}
                </li>
              )}
            </ul>
          )}
        </Card>

        {/* (c) Unlinked Aspire properties */}
        <Card title={t('inspectionCompletion.unlinkedTitle', 'Unlinked Aspire properties')} icon={<Link2Off size={15} />}>
          {unlinkedProps.length === 0 ? (
            <p className="py-6 text-center text-sm text-theme-secondary">
              {t('inspectionCompletion.unlinkedNone', 'Every Aspire property is linked in the app.')}
            </p>
          ) : (
            <>
              <p className="mb-2 text-xs text-theme-secondary">
                {t(
                  'inspectionCompletion.unlinkedHint',
                  'These exist in Aspire but not in the app, so no report can be created. Link them to start tracking.',
                )}
              </p>
              <ul className="divide-y divide-line">
                {unlinkedProps.slice(0, 12).map((p) => (
                  <li key={p.propertyId} className="flex items-center justify-between gap-2 py-2">
                    <span className="truncate text-sm font-medium text-theme-primary">
                      {p.name}
                      {p.branch && <span className="ml-1 text-xs font-normal text-theme-secondary">{p.branch}</span>}
                    </span>
                    <Pill tone="gray">
                      {t('inspectionCompletion.ticketsCount', { count: p.expected, defaultValue: '{{count}} tickets' })}
                    </Pill>
                  </li>
                ))}
                {unlinkedProps.length > 12 && (
                  <li className="pt-2 text-xs text-theme-secondary">
                    {t('inspectionCompletion.andMore', { count: unlinkedProps.length - 12, defaultValue: '+{{count}} more' })}
                  </li>
                )}
              </ul>
            </>
          )}
        </Card>
      </section>

      {/* 6. Recommendations ----------------------------------------------- */}
      {recommendations.length > 0 && (
        <Card title={t('inspectionCompletion.recTitle', 'Recommended actions')} icon={<Lightbulb size={15} />}>
          <ol className="space-y-2">
            {recommendations.map((r, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-theme-primary">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-500/20 text-xs font-bold text-blue-700 dark:text-blue-300">
                  {i + 1}
                </span>
                <span>{r}</span>
              </li>
            ))}
          </ol>
        </Card>
      )}

      {/* Detail modal (screen only) --------------------------------------- */}
      {detail && (
        <PropertyDetailModal
          property={detail}
          months={months}
          locale={locale}
          onClose={() => setDetail(null)}
          t={t}
          reportStatusLabel={reportStatusLabel}
          kindLabel={kindLabel}
        />
      )}
    </div>
  );
}

// Map a Tone to the matching text colour for inline figures.
function toneText(tone: Tone): string {
  switch (tone) {
    case 'emerald':
      return 'text-emerald-700 dark:text-emerald-300';
    case 'amber':
      return 'text-amber-700 dark:text-amber-300';
    case 'red':
      return 'text-red-600 dark:text-red-400';
    default:
      return 'text-theme-primary';
  }
}

// ---------------------------------------------------------------------------
// Detail modal — every month for one property (screen-only overlay).
// ---------------------------------------------------------------------------
function PropertyDetailModal({
  property,
  months,
  locale,
  onClose,
  t,
  reportStatusLabel,
  kindLabel,
}: {
  property: CompletionProperty;
  months: string[];
  locale: string;
  onClose: () => void;
  t: TFunction<'common'>;
  reportStatusLabel: (s: ReportCompletionStatus) => string;
  kindLabel: (k: CellKind) => string;
}) {
  function formatScheduled(d: string): string {
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return d;
    return dt.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });
  }

  function stateForCell(cell: CompletionMonthCell | undefined): { label: string; tone: Tone } {
    const k = cellKind(cell);
    const tone: Tone =
      k === 'approved'
        ? 'emerald'
        : k === 'submitted'
          ? 'blue'
          : k === 'missing'
            ? 'red'
            : k === 'upcoming'
              ? 'sky'
              : 'gray';
    return { label: kindLabel(k), tone };
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 print:hidden"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-xl border border-line bg-surface shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="sticky top-0 flex items-start justify-between gap-2 border-b border-line bg-surface px-4 py-3">
          <div>
            <h2 className="text-base font-bold text-theme-primary">{property.name}</h2>
            <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-theme-secondary">
              {property.branch && <Pill tone="gray">{property.branch}</Pill>}
              {!property.linked && (
                <Pill tone="amber" icon={<Link2Off size={11} />}>
                  {t('inspectionCompletion.unlinked', 'Unlinked')}
                </Pill>
              )}
              <span>
                {t('inspectionCompletion.modalRollup', {
                  submitted: property.submitted,
                  expected: property.expected,
                  pct: pct(property.completionRate),
                  defaultValue: '{{submitted}}/{{expected}} complete · {{pct}}%',
                })}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-theme-secondary hover:bg-subtle hover:text-theme-primary"
            aria-label={t('inspectionCompletion.close', 'Close')}
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-4 py-3">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-theme-secondary">
                <th className="py-2 pr-2 font-medium">{t('inspectionCompletion.modalMonth', 'Month')}</th>
                <th className="px-2 py-2 font-medium">{t('inspectionCompletion.modalScheduled', 'Scheduled')}</th>
                <th className="px-2 py-2 font-medium">{t('inspectionCompletion.modalTicket', 'Ticket')}</th>
                <th className="px-2 py-2 font-medium">{t('inspectionCompletion.modalAspire', 'Aspire')}</th>
                <th className="px-2 py-2 font-medium">{t('inspectionCompletion.modalReport', 'Report')}</th>
                <th className="px-2 py-2 font-medium">{t('inspectionCompletion.modalState', 'State')}</th>
              </tr>
            </thead>
            <tbody>
              {months.map((m) => {
                const cell = cellForMonth(property, m);
                const state = stateForCell(cell);
                return (
                  <tr key={m} className="border-b border-line last:border-0">
                    <td className="py-2 pr-2 font-medium text-theme-primary">{formatMonthLong(m, locale)}</td>
                    <td className="px-2 py-2 text-theme-secondary">{cell ? formatScheduled(cell.scheduledDate) : '—'}</td>
                    <td className="px-2 py-2 text-theme-secondary tabular-nums">
                      {cell?.ticketNumber != null ? `#${cell.ticketNumber}` : '—'}
                    </td>
                    <td className="px-2 py-2">
                      {cell?.aspireStatus ? (
                        <Pill tone={(cell.aspireStatus ?? '').toLowerCase() === 'complete' ? 'emerald' : 'gray'}>
                          {cell.aspireStatus}
                        </Pill>
                      ) : (
                        <span className="text-theme-secondary">—</span>
                      )}
                    </td>
                    <td className="px-2 py-2">
                      {cell ? (
                        <Pill tone={REPORT_TONE[cell.reportStatus]}>{reportStatusLabel(cell.reportStatus)}</Pill>
                      ) : (
                        <span className="text-theme-secondary">—</span>
                      )}
                    </td>
                    <td className="px-2 py-2">
                      <span className="inline-flex items-center gap-1.5">
                        {cell?.canceled && <Clock size={12} className="shrink-0 text-theme-secondary" />}
                        <Pill tone={state.tone}>{state.label}</Pill>
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
